
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ImplementationROIPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Implementation & ROI
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Strategic integration guidance for photobiomodulation recovery systems in clinical and commercial environments
          </p>
        </div>
      </section>

      {/* Space & Setup Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Space & Setup</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Space Requirements</h3>
              <ul className="space-y-3 text-gray-700">
                <li>• Single bed: 8' × 4' minimum floor space</li>
                <li>• Multi-bed room: Allow 3-4 feet between units</li>
                <li>• Ceiling height: Standard 8-9 feet adequate</li>
                <li>• Dedicated recovery area preferred</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Environmental Considerations</h3>
              <ul className="space-y-3 text-gray-700">
                <li>• Standard electrical requirements</li>
                <li>• Comfortable ambient temperature control</li>
                <li>• Appropriate lighting for relaxation</li>
                <li>• Sound management for patient comfort</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Operations & Scheduling Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Operations & Scheduling</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Workflow Integration</h3>
              <ul className="space-y-3 text-gray-700">
                <li>• Sessions typically 10-20 minutes</li>
                <li>• Minimal staff supervision required</li>
                <li>• Can run concurrent with other treatments</li>
                <li>• Easy integration with existing schedules</li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Scheduling Best Practices</h3>
              <ul className="space-y-3 text-gray-700">
                <li>• Block scheduling for efficiency</li>
                <li>• Pre-PT or post-PT timing</li>
                <li>• Between-visit recovery sessions</li>
                <li>• Dedicated recovery appointments</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Monetization Models Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Monetization Models</h2>
          <p className="text-gray-600 mb-8">Examples of common pricing strategies used by facilities:</p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Per-Session Add-On</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Individual session pricing</li>
                <li>• Easy to test and implement</li>
                <li>• Flexible for patients</li>
                <li>• Common range: $35-$65/session</li>
              </ul>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Recovery Bundles</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• 10-session packages</li>
                <li>• Volume pricing incentive</li>
                <li>• Encourages completion</li>
                <li>• Higher patient commitment</li>
              </ul>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Monthly Recovery Upgrade</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Membership add-on model</li>
                <li>• Recurring revenue stream</li>
                <li>• Unlimited or limited sessions</li>
                <li>• Premium service positioning</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Sample ROI Scenarios Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Sample ROI Scenarios</h2>
          
          <div className="bg-white rounded-lg shadow-sm p-8">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">Example PT Clinic Scenario</h3>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Setup Example:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• 3 beds installed</li>
                  <li>• $35–$65 per session pricing range</li>
                  <li>• 4–8 sessions per day utilization range</li>
                  <li>• 5-day operation schedule</li>
                </ul>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Potential Monthly Range:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Low scenario: 240 sessions/month</li>
                  <li>• High scenario: 480 sessions/month</li>
                  <li>• Revenue range: $8,400 - $31,200</li>
                  <li>• Varies with utilization and pricing</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <p className="text-sm text-yellow-800">
              <strong>Disclaimer:</strong> Examples are illustrative and depend on utilization, local demand, staffing, and pricing strategy. 
              Actual results will vary based on market conditions, implementation quality, and business operations.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to Explore Implementation?</h2>
          <p className="text-xl text-teal-100 mb-8">
            Get detailed implementation guidance and ROI projections for your specific facility
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact" 
              className="bg-white text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors duration-200 whitespace-nowrap"
            >
              Request a Quote
            </a>
            <a 
              href="/contact" 
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-teal-600 transition-colors duration-200 whitespace-nowrap"
            >
              Schedule a Facility Demo
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ImplementationROIPage;
